import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  dispatchCustomerError,
  dispatchCustomerSuccess,
  getIndividualCustomerDetail,
  updateCustomerData
} from '../../../services/customerServices';
import { isEmail, isEmpty, isNumber } from '../../../helpers/common';
import Button from '../../button';
import { captureException } from '../../../services/logService';
import { Formation, FormButton, ModalBlock } from './styles';
import { BUTTON_TYPE } from '../../../global/constants';
import { rootStore } from '../../../mobx/rootStore';
import ModalHeader from '../modalHeader';
import Input from '../../input';

interface Props {
  handleCloseModal: () => void;
}

const CustomerEditModal: React.FC<Props> = (props) => {
  const { handleCloseModal } = props;
  //States
  const [loading, setLoading] = useState(false);

  //Mobx store variables
  const { workspaceStore, customerStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const workspace_id = workspace?.id;
  const { newCustomer, individualCustomer } = customerStore;
  const { phone, email } = newCustomer || {};

  const loadData = useCallback(async () => {
    if (!isEmpty(workspace_id)) {
      await getIndividualCustomerDetail(individualCustomer?.id);
    }
  }, [workspace_id, individualCustomer?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onUpdateInputValue = useCallback(
    (propsName: string, value: string) => {
      customerStore.updateCustomerDetail(propsName, value);
    },
    [customerStore]
  );

  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setLoading(true);
        if (!isEmail(email) && !isEmpty(email)) {
          dispatchCustomerError('Please enter valid email address.');
          return;
        } else if (!isNumber(phone) && !isEmpty(phone)) {
          dispatchCustomerError('Please enter valid phone number.');
          return;
        }
        const updatePayload = {
          Email: email,
          Phone: phone
        };
        const response = await updateCustomerData(individualCustomer?.id, updatePayload);
        if (response) {
          dispatchCustomerSuccess('Customer details updated successfully!');
          handleCloseModal();
          customerStore.clearCustomerDetail();
          loadData();
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [email, phone, individualCustomer?.id, customerStore, handleCloseModal, loadData]
  );

  return (
    <ModalBlock onSubmit={onSubmit}>
      <ModalHeader title='Update Additional Details' onClose={handleCloseModal} />
      <Formation>
        <Input
          label='Company email'
          placeHolder='Enter company email'
          value={email}
          onChange={(e) => onUpdateInputValue('email', e.target.value)}
          type='email'
        />
        <Input
          label='Phone number'
          placeHolder='Enter phone number '
          value={phone}
          onChange={(e) => onUpdateInputValue('phone', e.target.value)}
          type='number'
        />
      </Formation>
      <FormButton>
        <Button title='Cancel' onClick={handleCloseModal} variant={BUTTON_TYPE.SECONDARY} buttonType='button' />
        <Button buttonType='submit' variant={BUTTON_TYPE.PRIMARY} title={'Update Customer'} isLoading={loading} />
      </FormButton>
    </ModalBlock>
  );
};

export default observer(CustomerEditModal);
